import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Alert, Button, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { isSignInWithEmailLink } from "firebase/auth";
import { auth } from "../firebase";
import { completeMagicLinkSignIn, getStoredEmailForSignIn } from "../api/auth";
import {
  getPostAuthDestination,
  syncMarketingPlanInterestFromSearchParams,
} from "../lib/billingSelection";
import { trackAuthComplete, trackMarketingPlanInterestCaptured } from "../lib/analytics";

export default function EmailLinkCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"checking" | "prompt_email" | "signing_in" | "error">(
    "checking"
  );
  const [emailPrompt, setEmailPrompt] = useState("");
  const [error, setError] = useState<string | null>(null);

  const trySignIn = async (email: string) => {
    setStatus("signing_in");
    try {
      await completeMagicLinkSignIn(email, window.location.href);
      trackAuthComplete("email_link", "/auth/email/callback");
      navigate(getPostAuthDestination(), { replace: true });
    } catch (err) {
      const isExpired =
        err instanceof Error && err.message.toLowerCase().includes("expired");
      setError(
        isExpired
          ? "This magic link has expired. Please request a new one."
          : "Sign in failed. Please try again."
      );
      setStatus("error");
    }
  };

  useEffect(() => {
    const marketingPlanInterest = syncMarketingPlanInterestFromSearchParams(new URLSearchParams(window.location.search));
    if (marketingPlanInterest) {
      trackMarketingPlanInterestCaptured(marketingPlanInterest, "/auth/email/callback");
    }

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      navigate("/login");
      return;
    }

    const storedEmail = getStoredEmailForSignIn();
    if (storedEmail) {
      trySignIn(storedEmail);
    } else {
      setStatus("prompt_email");
    }
  }, []);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (emailPrompt.trim()) trySignIn(emailPrompt.trim());
  };

  if (status === "checking" || status === "signing_in") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === "prompt_email") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Box component="form" onSubmit={handleEmailSubmit} sx={{ maxWidth: 400, width: "100%", p: 4 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>Confirm your email</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the email address you used to request this magic link.
          </Typography>
          <TextField
            label="Email address"
            type="email"
            fullWidth
            value={emailPrompt}
            onChange={(e) => setEmailPrompt(e.target.value)}
            required
            autoFocus
            sx={{ mb: 2 }}
          />
          <Button type="submit" variant="contained" fullWidth disabled={!emailPrompt.trim()}>
            Confirm
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 400, width: "100%", p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" fullWidth onClick={() => navigate("/login")}>
          Back to sign in
        </Button>
      </Box>
    </Box>
  );
}
